import { useCallback, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  clearNotificationBannerDismissed,
  dismissNotificationBanner,
  FCM_BLOCK_REASON,
  getFcmBlockReasonMessage,
  getNotificationPermission,
  isNotificationBannerDismissed,
  promptWebNotificationPermission,
  warmUpFcmMessagingServiceWorker,
} from '../services/fcmWebRegistration';
// ALERT_SOUND_DISABLED:
// import {
//   getPushEnableToastMessage,
//   tryPlayNotificationSoundInUserGesture,
// } from '../utils/notificationSound';

export const computeNotificationAlertStates = ({
  permission,
  blockReason,
  blockReasonMessage,
  isConnected,
} = {}) => {
  const hasBlockingConfig =
    blockReason === FCM_BLOCK_REASON.MISSING_VAPID ||
    blockReason === FCM_BLOCK_REASON.INSECURE_CONTEXT ||
    blockReason === FCM_BLOCK_REASON.SAFARI_IOS_PWA_REQUIRED ||
    blockReason === FCM_BLOCK_REASON.UNSUPPORTED_BROWSER;

  const showDenied = permission === 'denied';
  const showConfigIssue = blockReason === FCM_BLOCK_REASON.MISSING_VAPID;
  const showIosSafariPwa = blockReason === FCM_BLOCK_REASON.SAFARI_IOS_PWA_REQUIRED;
  const showUnsupported =
    blockReason === FCM_BLOCK_REASON.UNSUPPORTED_BROWSER && permission !== 'granted';
  const showInsecure = blockReason === FCM_BLOCK_REASON.INSECURE_CONTEXT;
  const showRegistrationRetry =
    !hasBlockingConfig &&
    permission === 'granted' &&
    !isConnected &&
    (blockReason === FCM_BLOCK_REASON.PUSH_SERVICE_UNAVAILABLE ||
      blockReason === FCM_BLOCK_REASON.TOKEN_FAILED);
  const needsPushSetup =
    !hasBlockingConfig &&
    permission !== 'denied' &&
    !showRegistrationRetry &&
    typeof Notification !== 'undefined' &&
    (permission === 'default' ||
      blockReason === FCM_BLOCK_REASON.PUSH_OPT_IN_REQUIRED ||
      blockReason === FCM_BLOCK_REASON.USER_GESTURE_REQUIRED ||
      (permission === 'granted' && !isConnected));

  const needsAttention =
    showDenied ||
    showConfigIssue ||
    showInsecure ||
    showIosSafariPwa ||
    showUnsupported ||
    showRegistrationRetry ||
    needsPushSetup;

  return {
    hasBlockingConfig,
    showDenied,
    showConfigIssue,
    showIosSafariPwa,
    showUnsupported,
    showInsecure,
    showRegistrationRetry,
    needsPushSetup,
    needsAttention,
    canEnableViaDialog: needsPushSetup || showRegistrationRetry,
    blockReason,
    blockReasonMessage,
  };
};

const INACTIVE_ALERT_STATES = { needsAttention: false };

/**
 * Push setup UI state shared by the bottom banner, header bell menu, and notifications page.
 */
export const useNotificationAlertsSetup = (
  push,
  { enabled = true, isAuthenticated = false, loading = false } = {},
) => {
  const [dismissed, setDismissed] = useState(isNotificationBannerDismissed);
  const [enabling, setEnabling] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentSubmitting, setConsentSubmitting] = useState(false);
  const consentResolverRef = useRef(null);

  const active = Boolean(enabled && isAuthenticated && !loading && push);
  const permission = push?.permission;
  const blockReason = push?.blockReason;
  const blockReasonMessage = push?.blockReasonMessage;
  const isConnected = push?.isConnected;

  const states = useMemo(
    () =>
      active
        ? computeNotificationAlertStates({
            permission,
            blockReason,
            blockReasonMessage,
            isConnected,
          })
        : INACTIVE_ALERT_STATES,
    [active, permission, blockReason, blockReasonMessage, isConnected],
  );

  const finishConsent = useCallback((result) => {
    setConsentOpen(false);
    setConsentSubmitting(false);
    const resolve = consentResolverRef.current;
    consentResolverRef.current = null;
    resolve?.(result);
  }, []);

  const requestAlertsConsent = useCallback(() => {
    void warmUpFcmMessagingServiceWorker();
    return new Promise((resolve) => {
      consentResolverRef.current = resolve;
      setConsentOpen(true);
    });
  }, []);

  const handleConsentCancel = useCallback(() => {
    if (consentSubmitting) {
      return;
    }
    finishConsent({ confirmed: false });
  }, [consentSubmitting, finishConsent]);

  const dismissBanner = useCallback(() => {
    dismissNotificationBanner();
    setDismissed(true);
  }, []);

  const restoreBanner = useCallback(() => {
    clearNotificationBannerDismissed();
    setDismissed(false);
  }, []);

  const finishEnableSuccess = useCallback(
    (consent) => {
      if (!consent?.confirmed || !consent?.pushResult?.success) {
        return;
      }

      toast.success('Browser notifications enabled', { duration: 6000 });
      // ALERT_SOUND_DISABLED — sound-aware toast:
      // const message = getPushEnableToastMessage({
      //   pushSuccess: true,
      //   soundEnabled: consent.soundEnabled,
      //   soundPlayed: consent.soundPlayed,
      // });
      // if (message) {
      //   toast.success(message, { duration: 6000 });
      // }
      dismissBanner();
    },
    [dismissBanner],
  );

  const handleConsentConfirm = useCallback(async () => {
    if (!push?.enablePush) {
      finishConsent({ confirmed: false });
      return;
    }

    // ALERT_SOUND_DISABLED — start test chime in same click as Continue (before permission prompt):
    // const soundEnabled = consentArgs?.soundEnabled ?? true;
    // const soundTask = soundEnabled
    //   ? tryPlayNotificationSoundInUserGesture()
    //   : Promise.resolve(false);

    setConsentOpen(false);
    setConsentSubmitting(true);

    try {
      if (getNotificationPermission() === 'default') {
        const permission = await promptWebNotificationPermission();
        if (permission !== 'granted') {
          // ALERT_SOUND_DISABLED — const soundPlayed = await soundTask;
          finishConsent({
            confirmed: true,
            pushResult: { success: false, permission, skipped: true },
            // ALERT_SOUND_DISABLED — soundEnabled, soundPlayed,
          });
          return;
        }
      }

      const result = await push.enablePush({
        forceRefresh: true,
        skipPermissionPrompt: true,
      });

      // ALERT_SOUND_DISABLED — const soundPlayed = await soundTask;
      finishConsent({ confirmed: true, pushResult: result });
    } catch (error) {
      // ALERT_SOUND_DISABLED — const soundPlayed = await soundTask;
      finishConsent({
        confirmed: true,
        pushResult: {
          success: false,
          message: error?.message || 'Push setup failed',
        },
        // ALERT_SOUND_DISABLED — soundEnabled, soundPlayed,
      });
    }
  }, [push, finishConsent]);

  const handleEnableAlerts = useCallback(async () => {
    setEnabling(true);
    try {
      const consent = await requestAlertsConsent();
      finishEnableSuccess(consent);
    } finally {
      setEnabling(false);
    }
  }, [requestAlertsConsent, finishEnableSuccess]);

  const handleRetryPush = useCallback(async () => {
    if (!push?.enablePush) {
      return;
    }

    setEnabling(true);
    try {
      const result = await push.enablePush({ forceRefresh: true, skipPermissionPrompt: true });
      if (result.success) {
        toast.success('Push notifications connected');
        void push.refreshStatus?.();
        dismissBanner();
        return;
      }
      if (result.reason) {
        toast.error(getFcmBlockReasonMessage(result.reason));
      } else if (result.message) {
        toast.error(result.message);
      }
    } finally {
      setEnabling(false);
    }
  }, [push, dismissBanner]);

  const startSetup = useCallback(() => {
    if (!active || !states.needsAttention) {
      return;
    }

    if (states.canEnableViaDialog) {
      if (states.showRegistrationRetry) {
        void handleRetryPush();
        return;
      }
      void handleEnableAlerts();
      return;
    }

    restoreBanner();
  }, [active, states, handleEnableAlerts, handleRetryPush, restoreBanner]);

  const isBusy = enabling || consentSubmitting;
  const showBanner = active && states.needsAttention && !dismissed;

  return {
    active,
    dismissed,
    dismissBanner,
    restoreBanner,
    startSetup,
    needsAttention: active && states.needsAttention,
    showBanner,
    states,
    isBusy,
    handleEnableAlerts,
    handleRetryPush,
    handleDismiss: dismissBanner,
    consentDialogProps: {
      open: consentOpen,
      onCancel: handleConsentCancel,
      onConfirm: handleConsentConfirm,
      isSubmitting: consentSubmitting,
    },
  };
};

export default useNotificationAlertsSetup;
